import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BlockStack, Card, FormLayout, Layout, Page, Select, Text, TextField } from '@shopify/polaris';
import { useAxiosInstance } from '../../context/AxiosContext';
import { useCreatePromotion, useUpdatePromotion, BANNERS } from './api';
import { useEditorForm } from './useEditorForm';
import AppPicker from './AppPicker';
import ImageField from './ImageField';
import BannerPreview from './BannerPreview';
import StatusBadge from './StatusBadge';
import UnsavedChanges from './UnsavedChanges';

const BLANK = {
  title: '', description: '', cta_text: '', cta_link: '', image_url: '',
  state: 'unpublished',
};

const STATES = [
  { label: 'Published', value: 'published' },
  { label: 'Unpublished', value: 'unpublished' },
  { label: 'Archived', value: 'archived' },
];

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:MM"; the API returns ISO8601 with a zone.

const BannerEditor = ({ banner, onClose }) => {
  const axiosInstance = useAxiosInstance();
  const isNew = !banner.id;

  const create = useCreatePromotion({ resource: BANNERS, modelKey: 'featured_banner', axiosInstance });
  const update = useUpdatePromotion({ resource: BANNERS, modelKey: 'featured_banner', axiosInstance });
  const mutation = isNew ? create : update;

  const {
    confirmingExit, dirty, discard, errorFor,
    keepEditing, leaveAnyway, requestClose, setField, submit, values,
  } = useEditorForm({
    record: banner,
    blank: BLANK,
    mutation,
    onClose,
    savedMessage: isNew ? 'Featured banner created' : 'Featured banner saved',
  });

  const objectUrl = useMemo(
    () => (values.image ? window.URL.createObjectURL(values.image) : null),
    [values.image],
  );
  useEffect(() => () => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  const preview = { ...values, previewImage: objectUrl || values.image_url };

  return (
    <Page
      fullWidth
      backAction={{ content: 'Featured banners', onAction: requestClose }}
      title={isNew ? 'New featured banner' : (values.title || 'Featured banner')}
      titleMetadata={banner.status ? <StatusBadge status={banner.status} /> : undefined}
    >
      <Layout>
        {/* Full width, not the sidebar: the banner is 948px on the merchant page and reading it at
            any other size tells you nothing about how the text and image actually sit. */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">Merchant preview</Text>
              <BannerPreview values={preview} />
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="400">

            <Card>
              <BlockStack gap="200">
                <Select
                  label="Status"
                  options={STATES}
                  value={values.state}
                  onChange={setField('state')}
                  error={errorFor('state')}
                />
                <Text as="span" variant="bodySm" tone="subdued">
                  Only a published banner appears, inside its dates and in the apps picked below.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingSm">Banner content</Text>

                <ImageField
                  label="Banner image"
                  required
                  shape="wide"
                  exclusive
                  url={values.image_url}
                  file={values.image}
                  onUrlChange={setField('image_url')}
                  onFileChange={setField('image')}
                  error={errorFor('image_url')}
                  hint="Landscape, 340 px wide or larger. Displayed at 170 px beside the text."
                />

                <FormLayout>
                  <TextField
                    label="Title"
                    requiredIndicator
                    autoComplete="off"
                    value={values.title || ''}
                    onChange={setField('title')}
                    error={errorFor('title')}
                  />

                  <TextField
                    label="Description"
                    requiredIndicator
                    multiline={3}
                    autoComplete="off"
                    value={values.description || ''}
                    onChange={setField('description')}
                    error={errorFor('description')}
                  />

                  <FormLayout.Group>
                    <TextField
                      label="Button text"
                      requiredIndicator
                      autoComplete="off"
                      value={values.cta_text || ''}
                      onChange={setField('cta_text')}
                      error={errorFor('cta_text')}
                    />
                    <TextField
                      label="Button link"
                      requiredIndicator
                      autoComplete="off"
                      helpText="Must start with https://"
                      value={values.cta_link || ''}
                      onChange={setField('cta_link')}
                      error={errorFor('cta_link')}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <AppPicker
                  codes={values.app_codes}
                  overrides={values.overrides}
                  onChange={setField('app_codes')}
                  onOverridesChange={setField('overrides')}
                  error={errorFor('app_dashboard')}
                />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        {/* The form keeps the two-thirds width the card editor has, so the two pages read the same.
            The preview cannot sit in this column - 948px does not fit - so the column stays empty. */}
        <Layout.Section variant="oneThird" />
      </Layout>

      <UnsavedChanges
        confirmingExit={confirmingExit}
        dirty={dirty}
        saving={mutation.isLoading}
        onSave={submit}
        onDiscard={discard}
        onKeep={keepEditing}
        onLeave={leaveAnyway}
      />

    </Page>
  );
};

BannerEditor.propTypes = {
  banner: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BannerEditor;
